// src/pages/ResetPassword.jsx
import { useState } from 'react';
import { api } from '../utilities/api';
import { toast } from 'react-hot-toast';
import { validatePassword } from '../utilities/utilities';

function ResetPassword({onSaveComplete, onSaveSuccess, onDirty}) {
  const [formData, setFormData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);  

  const handlePasswordChange = (name, value) => {
    onDirty?.();
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      setError('Passwords do not match');
      return;
    }
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      setError(passwordError);
      return;
    }
    try {
      setIsSubmitting(true);
      const result = await api.changePassword({
        "oldPassword" : formData.oldPassword,
        "newPassword" : formData.newPassword,
        "confirmPassword" : formData.confirmPassword,
      });
      if (result.success === true) {
        toast.success('Password changed successfully');
        setMessage('Password reset successfully');
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setShowOldPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        onSaveSuccess?.();
      } else {
        toast.error(result.error || "Failed to reset password.");
        setError(result.error || 'Failed to reset password.');
      }
    } catch {
      setError('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

return (
  <div className="flex flex-grow flex-col items-center min-h-full w-full px-4 py-8">
    <div className="w-full max-w-md mx-auto">
    <form id="profile-password-form" onSubmit={handleSubmit} className="w-full">
      <h2 className="text-2xl font-bold text-primary mb-6">Set New Password</h2>
      {/* Old Password */}
      <div className="relative mb-4">
        <label htmlFor="oldPassword" className="sr-only">Old Password</label>
        <input
          id="oldPassword"
          name="oldPassword"
          type={showOldPassword ? "text" : "password"}
          placeholder="Old Password"
          value={formData.oldPassword}
          onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
          autoComplete="current-password"
          className="w-full p-2 pr-10 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary"
          required
        />
        <button
          type="button"
          onClick={() => setShowOldPassword(!showOldPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
          aria-label={showOldPassword ? 'Hide old password' : 'Show old password'}
        >
          {showOldPassword ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>

      {/* New Password */}
      <div className="relative mb-4">
        <label htmlFor="newPassword" className="sr-only">New Password</label>
        <input
          id="newPassword"
          name="newPassword"
          type={showNewPassword ? "text" : "password"}
          placeholder="New Password"
          value={formData.newPassword}
          onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
          autoComplete="new-password"
          minLength={8}
          className="w-full p-2 pr-10 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary"
          required
        />
        <button
          type="button"
          onClick={() => setShowNewPassword(!showNewPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
          aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
        >
          {showNewPassword ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>

      {/* Confirm New Password */}
      <div className="relative mb-4">
        <label htmlFor="confirmPassword" className="sr-only">Confirm New Password</label>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirm New Password"
          value={formData.confirmPassword}
          onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
          autoComplete="new-password"
          minLength={8}
          className="w-full p-2 pr-10 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary"
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
          aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
        >
          {showConfirmPassword ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>
      {message && <p className="text-green-500 text-sm">{message}</p>}
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </form>

    <div className="flex w-full flex-row justify-end items-center gap-4 pt-4">
      <button
        type="button"
        onClick={onSaveComplete}
        className={`w-1/2 bg-neutral-light text-neutral-dark p-3 rounded hover:bg-neutral-dark hover:text-white transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={isSubmitting}
      >
        {isSubmitting ? '' : 'Close'}
      </button>
      <button
        type="submit"
        form="profile-password-form"
        className={`w-1/2 bg-button text-white p-3 rounded-md hover:bg-button-light transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Saving...' : 'Change Password'}
      </button>
    </div>
    </div>
  </div>
);
}

export default ResetPassword;
